// The note vault: a staging area between "I noticed something" and "it's in
// my Obsidian vault".
//
// ── THE PERSISTENCE RULE ─────────────────────────────────────────────────────
// Queued notes are IN MEMORY ONLY. They embed genotype calls, and the whole
// promise of this app is that your genome does not survive a reload. Writing
// them to localStorage would quietly break that.
//
// The ideas ledger IS persisted, because it is your own prose and a ledger that
// forgets is useless. It never contains a genotype unless you type one, and the
// UI says so plainly. That asymmetry is deliberate, not an oversight.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type NoteKind = 'variant' | 'summary' | 'condition';

export interface QueuedNote {
  id: string;
  kind: NoteKind;
  /** Display title, e.g. "rs1815739" or "Type 2 diabetes". */
  title: string;
  /** Obsidian-safe file stem, without the .md. */
  filename: string;
  /** The full markdown body. */
  body: string;
}

export interface Idea {
  id: string;
  text: string;
  /** ISO date, so the exported ledger is dated. */
  created: string;
}

interface VaultState {
  notes: QueuedNote[];
  ideas: Idea[];
  queueNote: (note: Omit<QueuedNote, 'id'>) => void;
  removeNote: (id: string) => void;
  clearNotes: () => void;
  addIdea: (text: string) => void;
  removeIdea: (id: string) => void;
}

const VaultContext = createContext<VaultState | null>(null);

const IDEAS_KEY = 'i-seng:ideas';

function readIdeas(): Idea[] {
  try {
    const raw = localStorage.getItem(IDEAS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (i): i is Idea =>
        typeof i === 'object' && i !== null && typeof (i as Idea).text === 'string',
    );
  } catch {
    return [];
  }
}

/** Monotonic ids without Date.now() collisions when queueing in a tight loop. */
let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

export function VaultProvider({ children }: { children: ReactNode }) {
  const [notes, setNotes] = useState<QueuedNote[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>(readIdeas);

  useEffect(() => {
    try {
      localStorage.setItem(IDEAS_KEY, JSON.stringify(ideas));
    } catch {
      /* storage disabled — the ledger just won't survive a reload */
    }
  }, [ideas]);

  const queueNote = useCallback((note: Omit<QueuedNote, 'id'>) => {
    setNotes((current) => {
      // Re-queueing the same thing should refresh it, not duplicate it.
      const without = current.filter((n) => n.filename !== note.filename);
      return [...without, { ...note, id: nextId('note') }];
    });
  }, []);

  const removeNote = useCallback((id: string) => {
    setNotes((current) => current.filter((n) => n.id !== id));
  }, []);

  const clearNotes = useCallback(() => setNotes([]), []);

  const addIdea = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setIdeas((current) => [
      { id: nextId('idea'), text: trimmed, created: new Date().toISOString().slice(0, 10) },
      ...current,
    ]);
  }, []);

  const removeIdea = useCallback((id: string) => {
    setIdeas((current) => current.filter((i) => i.id !== id));
  }, []);

  const value = useMemo(
    () => ({ notes, ideas, queueNote, removeNote, clearNotes, addIdea, removeIdea }),
    [notes, ideas, queueNote, removeNote, clearNotes, addIdea, removeIdea],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault(): VaultState {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used inside <VaultProvider>');
  return ctx;
}

/** The ideas ledger as one dated markdown note. */
export function ideasLedger(ideas: Idea[], today: string): string {
  return [
    '---',
    'type: ideas-ledger',
    'source: i-seng',
    `updated: ${today}`,
    'tags: [genome, ideas]',
    '---',
    '',
    '# Genome ideas ledger',
    '',
    '> Questions, hunches, and things to follow up. Captured in i-seng.',
    '',
    ...(ideas.length
      ? ideas.map((i) => `- [ ] ${i.text} _(${i.created})_`)
      : ['_Nothing captured yet._']),
    '',
    '---',
    'Related: [[Genome Index]]',
    '',
  ].join('\n');
}

/**
 * Concatenate queued notes into one file, separated so they can be split back
 * apart. Obsidian will happily take this as a single note too.
 */
export function bundleNotes(notes: QueuedNote[]): string {
  return notes
    .map((n) => `<!-- ── ${n.filename}.md ── -->\n\n${n.body}`)
    .join('\n\n---\n\n');
}

/**
 * Save text as a file, entirely locally.
 *
 * A blob: URL on an anchor with `download` is a navigation, not a fetch, so
 * `connect-src 'none'` does not block it — the bytes never leave the process.
 * Returns false if the browser refuses, so the caller can fall back to the
 * clipboard.
 */
export function downloadText(filename: string, text: string): boolean {
  try {
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke on the next tick so the download has started.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}
